// AdminContext.js
// Provides admin mode state management for support debugging and concierge features

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// Hardcoded admin user IDs (add your admin UIDs here)
// In v1, this is the simplest approach - no role management UI needed
export const ADMIN_USER_IDS = [
  "1Gi4GN96Y5hcfjhgTPhxzbe3Usp1"
];

// Concierge sender options for admin comments
export const CONCIERGE_SENDERS = [
  { id: 'kana', name: 'Kana', avatar: null },
  { id: 'david', name: 'David', avatar: null },
];

const AdminContext = createContext(null);

export function AdminProvider({ children, userId }) {
  // Is current user an admin?
  const [isAdmin, setIsAdmin] = useState(false);

  // Family being viewed in admin mode (null = not viewing any family)
  const [viewingFamilyId, setViewingFamilyId] = useState(null);

  // Family data for the viewed family
  const [viewingFamilyData, setViewingFamilyData] = useState(null);

  // Loading state while fetching family data
  const [loadingFamily, setLoadingFamily] = useState(false);

  // Error state
  const [adminError, setAdminError] = useState(null);

  // Check if user is admin
  useEffect(() => {
    setIsAdmin(ADMIN_USER_IDS.includes(userId));
  }, [userId]);

  // Fetch family data when viewingFamilyId changes
  useEffect(() => {
    if (!viewingFamilyId) {
      setViewingFamilyData(null);
      return;
    }

    setLoadingFamily(true);
    setAdminError(null);

    const fetchFamilyData = async () => {
      try {
        const familyDoc = await getDoc(doc(db, 'families', viewingFamilyId));
        if (familyDoc.exists()) {
          setViewingFamilyData({ id: familyDoc.id, ...familyDoc.data() });
        } else {
          setAdminError('Family not found');
          setViewingFamilyId(null);
        }
      } catch (error) {
        console.error('Error fetching family for admin view:', error);
        setAdminError('Failed to load family');
        setViewingFamilyId(null);
      } finally {
        setLoadingFamily(false);
      }
    };

    fetchFamilyData();
  }, [viewingFamilyId]);

  // Load a family by ID for admin viewing
  const loadFamily = useCallback(async (familyId) => {
    if (!isAdmin) {
      setAdminError('Not authorized');
      return false;
    }

    setLoadingFamily(true);
    setAdminError(null);

    try {
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      if (familyDoc.exists()) {
        setViewingFamilyId(familyId);
        setViewingFamilyData({ id: familyDoc.id, ...familyDoc.data() });
        return true;
      } else {
        setAdminError('Family not found');
        return false;
      }
    } catch (error) {
      console.error('Error loading family:', error);
      setAdminError('Failed to load family');
      return false;
    } finally {
      setLoadingFamily(false);
    }
  }, [isAdmin]);

  // Exit admin view mode
  const exitAdminView = useCallback(() => {
    setViewingFamilyId(null);
    setViewingFamilyData(null);
    setAdminError(null);
  }, []);

  // Check if currently in admin view mode
  const isInAdminView = isAdmin && viewingFamilyId !== null;

  const value = {
    isAdmin,
    isInAdminView,
    viewingFamilyId,
    viewingFamilyData,
    loadingFamily,
    adminError,
    loadFamily,
    exitAdminView,
    setAdminError,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

export default AdminContext;
